import type { ReactNode } from 'react'

import type { SortOrder } from '@/api/list'
import { DataTable } from '@/design'
import type { TableSort } from '@/design/DataTable'

import { ColumnChooser } from './ColumnChooser'
import { useColumnChoice, type ColumnChoiceConfig } from './columnChoice'
import { Footer, type Paging } from './ListStates'
import { Notes } from './Notes'
import { columnState, tableColumns, type DescribeReason, type ListColumn, type NotAvailable, type ReasonRule } from './nullState'
import './list.css'

/**
 * A server-sorted list's table and what sits around it: the toolbar with the column
 * chooser, the notes, the table and the footer. The column the list is sorted by is
 * shown even when not chosen, so the order on screen can be read; a header click asks
 * `onSort` for the server's next order rather than reordering the rows on screen.
 */
export function ListTable<Row extends object, Context extends { notAvailable: NotAvailable<Row, Reason> }, Reason extends string>({
  caption,
  columns,
  groupLabels,
  columnChoice,
  sort,
  onSort,
  rows,
  rowKey,
  rowHref,
  context,
  cells,
  rule,
  describe,
  lead,
  toolbarStart,
  toolbarEnd,
  empty,
  footer,
}: {
  caption: string
  /** Every column the list can show, in display order; the first is always shown. */
  columns: readonly ListColumn<Row, Context>[]
  groupLabels: Readonly<Record<string, string>>
  columnChoice: ColumnChoiceConfig
  /** The list's server sort: a column's `sort` key, and the direction. */
  sort: { sort: string; order: SortOrder }
  /** A column's `sort` key and direction for the next order; `null` restores the default. */
  onSort: (next: { sort: string; order: SortOrder } | null) => void
  rows: readonly Row[]
  rowKey: (row: Row) => string
  rowHref?: (row: Row) => string
  /** Data-only: the null rule's reasons and how figures read. */
  context: Context
  /** A page's own rendering of a column's cells, by column key, in place of the column's. */
  cells?: Readonly<Partial<Record<string, (row: Row) => ReactNode>>>
  rule: ReasonRule<Row, Reason>
  /** How the list words each reason, on cells, headers, the chooser and the notes alike. */
  describe: DescribeReason<Reason>
  /** A sentence ahead of the notes, for the columns shown. */
  lead?: (visible: readonly ListColumn<Row, Context>[]) => ReactNode
  toolbarStart?: ReactNode
  /** Controls ahead of the column chooser. */
  toolbarEnd?: ReactNode
  empty: ReactNode
  /** The count and the next page; null for a list that is one page. */
  footer: { total: number; noun: string; paging: Paging } | null
}) {
  const choice = useColumnChoice(columnChoice)
  const { notAvailable } = context
  const sortColumn = columns.find((column) => column.sort === sort.sort)
  const visible = columns
    .filter((column) => choice.chosen.includes(column.key) || column === sortColumn)
    .map((column) => {
      const own = cells?.[column.key]
      return own ? { ...column, cell: (row: Row) => own(row) } : column
    })
  const tableSort: TableSort | null = sortColumn ? { key: sortColumn.key, direction: sort.order } : null

  const onSortChange = (next: TableSort | null) => {
    const column = next ? columns.find((candidate) => candidate.key === next.key) : undefined
    onSort(next && column?.sort ? { sort: column.sort, order: next.direction } : null)
  }

  return (
    <div className="list-body">
      <div className={toolbarStart ? 'list-toolbar' : 'list-toolbar list-toolbar--end'}>
        {toolbarStart}
        <div className="list-toolbar__end">
          {toolbarEnd}
          <ColumnChooser
            columns={columns}
            groupLabels={groupLabels}
            chosen={choice.chosen}
            sorted={sortColumn}
            visibleCount={visible.length}
            missing={(column) => {
              const state = columnState(column, rows, notAvailable, rule)
              return state?.whole ? describe(state.reason).label : null
            }}
            onToggle={choice.toggle}
            onReset={choice.reset}
          />
        </div>
      </div>
      <Notes columns={visible} rows={rows} notAvailable={notAvailable} rule={rule} describe={describe} lead={lead?.(visible)} />
      <DataTable
        caption={caption}
        columns={tableColumns(visible, rows, context, rule, describe)}
        rows={rows}
        rowKey={rowKey}
        rowHref={rowHref}
        sortMode="controlled"
        sort={tableSort}
        onSortChange={onSortChange}
        empty={empty}
      />
      {footer && rows.length > 0 ? <Footer shown={rows.length} total={footer.total} noun={footer.noun} paging={footer.paging} /> : null}
    </div>
  )
}
